/**
 * Web Audio API-backed implementation.
 *
 * Replaced ToneAudio at milestone 1.1. We only ever needed:
 *   - a continuous low snore loop (a sine osc with slow-LFO gain)
 *   - a short impact thud (a low oscillator with exponential gain decay)
 * All of that is ~100 lines of vanilla Web Audio. Tone.js was adding
 * ~150 KB to the bundle and eagerly creating an AudioContext on import
 * (the warnings you saw before any user gesture), which we don't want.
 *
 * Audio context is created lazily on the first `start()` call from a
 * user gesture, per browser autoplay policy.
 */

import type { AudioEvent, IAudio } from './index.js';

const SNORE_BASE_HZ = 60;
const SNORE_PERIOD_SECONDS = 4.5;
/** Volume bounds for snore loop: silent ↔ quiet. It's a sleeping person. */
const SNORE_MIN_GAIN = 0.0;
const SNORE_PEAK_GAIN = 0.025;
const PURR_BASE_HZ = 28;
const PURR_MIN_GAIN = 0.0;
const PURR_PEAK_GAIN = 0.018;

export class WebAudioAudio implements IAudio {
  private ctx: AudioContext | null = null;
  /** Snore master gain that we ramp with intensity. */
  private snoreOutGain: GainNode | null = null;
  /** LFO gain that envelopes the snore oscillator (breathing). */
  private snoreEnvGain: GainNode | null = null;
  private snoreOsc: OscillatorNode | null = null;
  private snoreLfo: OscillatorNode | null = null;
  private snoreLfoGain: GainNode | null = null;

  private purrOutGain: GainNode | null = null;
  private purrOsc: OscillatorNode | null = null;

  private comfortHumGain: GainNode | null = null;
  private comfortHumOsc: OscillatorNode | null = null;

  private snoreIntensity = 0;
  private purrIntensity = 0;
  private started = false;

  /** Start the audio engine. Must be called from a user-gesture handler. */
  async start(): Promise<void> {
    if (this.started) return;
    // `webkitAudioContext` is a Safari < 14 legacy alias; we still target
    // it for older mobile Safari that may visit the build.
    const Ctor =
      typeof AudioContext !== 'undefined'
        ? AudioContext
        : (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return; // Hostile environment — degrade silently.
    this.ctx = new Ctor();
    // Some browsers start the context in 'suspended' state until a gesture
    // explicitly resumes it. The caller is in a user-gesture handler so
    // this is the right moment.
    if (this.ctx.state === 'suspended') await this.ctx.resume();

    this.buildSnoreChain();
    this.buildPurrChain();
    this.buildComfortHumChain();
    this.started = true;
    this.applySnoreIntensity();
    this.applyPurrIntensity();
  }

  /** snore_osc → snore_env (LFO-modulated) → snore_out → destination */
  private buildSnoreChain(): void {
    if (!this.ctx) return;
    const ctx = this.ctx;

    // Carrier — low sine.
    this.snoreOsc = ctx.createOscillator();
    this.snoreOsc.type = 'sine';
    this.snoreOsc.frequency.value = SNORE_BASE_HZ;

    // Breathing envelope: an LFO modulates a gain node from 0 → 1.
    // (Web Audio oscillators output -1..+1; we route through a gain
    // so the envelope rests at 0 instead of -1.)
    this.snoreLfo = ctx.createOscillator();
    this.snoreLfo.type = 'sine';
    this.snoreLfo.frequency.value = 1 / SNORE_PERIOD_SECONDS;
    this.snoreLfoGain = ctx.createGain();
    this.snoreLfoGain.gain.value = 0.5;

    this.snoreEnvGain = ctx.createGain();
    this.snoreEnvGain.gain.value = 0.5; // baseline; LFO adds ±0.5 on top

    // LFO drives the env-gain's gain audio-param.
    this.snoreLfo.connect(this.snoreLfoGain);
    this.snoreLfoGain.connect(this.snoreEnvGain.gain);

    // Carrier → env → master snore gain → destination.
    this.snoreOutGain = ctx.createGain();
    this.snoreOutGain.gain.value = 0;
    this.snoreOsc.connect(this.snoreEnvGain);
    this.snoreEnvGain.connect(this.snoreOutGain);
    this.snoreOutGain.connect(ctx.destination);

    this.snoreOsc.start();
    this.snoreLfo.start();
  }

  /** Low rumble pad — intensity driven by calm purring cats. */
  private buildPurrChain(): void {
    if (!this.ctx) return;
    const ctx = this.ctx;
    this.purrOsc = ctx.createOscillator();
    this.purrOsc.type = 'triangle';
    this.purrOsc.frequency.value = PURR_BASE_HZ;
    this.purrOutGain = ctx.createGain();
    this.purrOutGain.gain.value = 0;
    this.purrOsc.connect(this.purrOutGain);
    this.purrOutGain.connect(ctx.destination);
    this.purrOsc.start();
  }

  /** Soft body-contact layer during comfort hold. */
  private buildComfortHumChain(): void {
    if (!this.ctx) return;
    const ctx = this.ctx;
    this.comfortHumOsc = ctx.createOscillator();
    this.comfortHumOsc.type = 'sine';
    this.comfortHumOsc.frequency.value = 52;
    this.comfortHumGain = ctx.createGain();
    this.comfortHumGain.gain.value = 0;
    this.comfortHumOsc.connect(this.comfortHumGain);
    this.comfortHumGain.connect(ctx.destination);
    this.comfortHumOsc.start();
  }

  emit(event: AudioEvent): void {
    if (!this.started || !this.ctx) return;
    switch (event) {
      case 'cat_landed_thud':
      case 'item_dropped_sharp':
        this.playImpact(55, 0.18, 0.35);
        break;
      case 'cat_impact_on_cat':
        this.playImpact(70, 0.16, 0.28);
        break;
      case 'cat_landed_soft':
        this.playImpact(110, 0.08, 0.14);
        break;
      // Reserved for milestone 2+.
      case 'fur_ruffle':
        this.playImpact(220, 0.06, 0.08);
        break;
      case 'comfort_hum_engage':
        this.setComfortHum(0.012);
        break;
      case 'comfort_hum_release':
        this.setComfortHum(0);
        break;
      case 'treat_applied':
        this.playImpact(180, 0.12, 0.12);
        break;
      case 'catnip_applied':
        this.playImpact(140, 0.2, 0.1);
        break;
      case 'toy_glint':
        this.playImpact(300, 0.04, 0.05);
        break;
      case 'toy_curious':
        this.playImpact(160, 0.1, 0.09);
        break;
      case 'toy_alert':
        this.playImpact(90, 0.25, 0.2);
        break;
    }
  }

  /** A short percussive thud: a sine that drops in pitch + exponential
   *  gain decay. Cheap to schedule, sounds reasonable for greybox. */
  private playImpact(freqHz: number, decaySeconds: number, peakGain: number): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freqHz, now);
    // Slight pitch drop for "thud" feel.
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, freqHz * 0.5), now + decaySeconds);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(peakGain, now + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + decaySeconds);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + decaySeconds + 0.02);
    osc.onended = (): void => {
      // Disconnect so the GC can reclaim — Web Audio nodes leak if held.
      osc.disconnect();
      gain.disconnect();
    };
  }

  setSnoreIntensity(value: number): void {
    this.snoreIntensity = Math.max(0, Math.min(1, value));
    this.applySnoreIntensity();
  }

  setPurrIntensity(value: number): void {
    this.purrIntensity = Math.max(0, Math.min(1, value));
    this.applyPurrIntensity();
  }

  private setComfortHum(target: number): void {
    if (!this.ctx || !this.comfortHumGain) return;
    const now = this.ctx.currentTime;
    this.comfortHumGain.gain.cancelScheduledValues(now);
    this.comfortHumGain.gain.setValueAtTime(this.comfortHumGain.gain.value, now);
    this.comfortHumGain.gain.linearRampToValueAtTime(target, now + 0.5);
  }

  private applyPurrIntensity(): void {
    if (!this.ctx || !this.purrOutGain) return;
    const target = PURR_MIN_GAIN + (PURR_PEAK_GAIN - PURR_MIN_GAIN) * this.purrIntensity;
    const now = this.ctx.currentTime;
    this.purrOutGain.gain.cancelScheduledValues(now);
    this.purrOutGain.gain.setValueAtTime(this.purrOutGain.gain.value, now);
    this.purrOutGain.gain.linearRampToValueAtTime(target, now + 0.4);
  }

  private applySnoreIntensity(): void {
    if (!this.ctx || !this.snoreOutGain) return;
    const target = SNORE_MIN_GAIN + (SNORE_PEAK_GAIN - SNORE_MIN_GAIN) * this.snoreIntensity;
    const now = this.ctx.currentTime;
    // Smooth ramp so adjustments don't click.
    this.snoreOutGain.gain.cancelScheduledValues(now);
    this.snoreOutGain.gain.setValueAtTime(this.snoreOutGain.gain.value, now);
    this.snoreOutGain.gain.linearRampToValueAtTime(target, now + 0.3);
  }

  destroy(): void {
    try {
      this.snoreOsc?.stop();
      this.snoreLfo?.stop();
      this.purrOsc?.stop();
      this.comfortHumOsc?.stop();
    } catch {
      // Already stopped — ignore.
    }
    this.snoreOsc?.disconnect();
    this.snoreLfo?.disconnect();
    this.snoreLfoGain?.disconnect();
    this.snoreEnvGain?.disconnect();
    this.snoreOutGain?.disconnect();
    this.purrOsc?.disconnect();
    this.purrOutGain?.disconnect();
    this.comfortHumOsc?.disconnect();
    this.comfortHumGain?.disconnect();
    void this.ctx?.close();
    this.ctx = null;
    this.snoreOsc = null;
    this.snoreLfo = null;
    this.snoreLfoGain = null;
    this.snoreEnvGain = null;
    this.snoreOutGain = null;
    this.started = false;
  }
}
